import { useState, useEffect } from 'react';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface SanitiseCalculatorProps {
    basePrice: number;
    onCalculate: (service: { title: string; price: string }) => void;
    premiseType: string;
    selectedServices: Set<{ title: string; price: string }>;
}

const formatTextWithItalics = (text: string): ReactNode => {
    if (!text.includes('`')) return text;

    const parts = text.split('`');
    const result: React.ReactNode[] = [];

    parts.forEach((part, index) => {
        if (index % 2 === 0) {
            result.push(part);
        } else {
            result.push(<i key={index}>{part}</i>);
        }
    });

    return result;
};

const SanitiseCalculator = ({ basePrice, onCalculate, premiseType, selectedServices }: SanitiseCalculatorProps) => {
    const { t } = useTranslation();
    const [dimensions, setDimensions] = useState({ length: '', width: '' });
    const [unit, setUnit] = useState<'cm'|'inch'|'sqft'>('sqft');
    const [showCalculator, setShowCalculator] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [selectedSqft, setSelectedSqft] = useState<number>(0);

    // Convert measurements to sqft
    const convertToSqft = (length: number, width: number, unit: string): number => {
        switch(unit) {
            case 'cm':
                return (length * width) / 929.0304; // 1 sqft = 929.0304 sq cm
            case 'inch':
                return (length * width) / 144; // 1 sqft = 144 sq inches
            default:
                return length * width;
        }
    };

    // Calculate total sqft
    const calculateSqft = (): number => {
        const length = parseFloat(dimensions.length);
        const width = parseFloat(dimensions.width);
        if (isNaN(length) || isNaN(width)) return 0;
        return convertToSqft(length, width, unit);
    };

    useEffect(() => {
        const existingService = Array.from(selectedServices).find(service => 
            service.title.startsWith(premiseType)
        );

        if (existingService) {
            setIsSelected(true);
            const match = existingService.title.match(/\(([\d.]+) sqft\)/);
            if (match) {
                const sqft = parseFloat(match[1]);
                setSelectedSqft(sqft);
                // Set dimensions for sqft unit (square root for length/width)
                const side = Math.sqrt(sqft);
                setDimensions({ length: side.toString(), width: side.toString() });
                setUnit('sqft');
            }
        } else {
            setIsSelected(false);
            setSelectedSqft(0);
            setDimensions({ length: '', width: '' });
        }
    }, [selectedServices, premiseType]);

    const handleCalculate = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const totalSqft = calculateSqft();
        if (totalSqft > 0) {
            const totalPrice = (basePrice * totalSqft).toFixed(2);
            const sanitiseService = {
                title: `${formatTextWithItalics(premiseType)} (${totalSqft.toFixed(2)} sqft)`,
                price: `RM${totalPrice}`
            };
            onCalculate(sanitiseService);
            setShowCalculator(false);
            setIsSelected(true);
            setSelectedSqft(totalSqft);
        }
    };

    const handleToggle = () => {
        if (isSelected) {
            onCalculate({ title: premiseType, price: '' });
            setIsSelected(false);
            setSelectedSqft(0);
            setDimensions({ length: '', width: '' });
        } else {
            setShowCalculator(!showCalculator);
        }
    };

    return (
        <div>
            <button
                type="button"
                onClick={handleToggle}
                className={`px-3 py-1.5 rounded-md text-xs flex items-center ${
                    isSelected 
                        ? 'bg-blue-800 text-white'
                        : showCalculator 
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700'
                }`}
            >
                {isSelected 
                    ? `${formatTextWithItalics(premiseType)} (${selectedSqft.toFixed(2)} sqft) - RM${(basePrice * selectedSqft).toFixed(2)}`
                    : formatTextWithItalics(premiseType)
                }
            </button>

            {showCalculator && !isSelected && (
                <div className="mt-2">
                    <div className="space-y-2">
                        <label className="block text-sm text-gray-700">
                            {t('calculators.sanitise.sizeLabel')} {formatTextWithItalics(premiseType)}
                        </label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="number"
                                min="0"
                                value={dimensions.length}
                                onChange={(e) => setDimensions(prev => ({ ...prev, length: e.target.value }))}
                                className="w-full border rounded-md p-2 h-10 text-sm"
                                placeholder={t('calculators.sanitise.length')}
                            />
                            <span className="text-sm">×</span>
                            <input
                                type="number"
                                min="0"
                                value={dimensions.width}
                                onChange={(e) => setDimensions(prev => ({ ...prev, width: e.target.value }))}
                                className="w-full border rounded-md p-2 h-10 text-sm"
                                placeholder={t('calculators.sanitise.width')}
                            />
                            <select
                                value={unit}
                                onChange={(e) => setUnit(e.target.value as 'cm'|'inch'|'sqft')}
                                className="border rounded-md p-2 h-10 text-sm"
                            >
                                <option className='italic' value="cm">cm</option>
                                <option className='italic' value="inch">inch</option>
                                <option className='italic' value="sqft">sqft</option>
                            </select>
                        </div>
                        {calculateSqft() > 0 && (
                            <div className="text-sm font-medium">
                                {t('calculators.sanitise.total')} RM{(basePrice * calculateSqft()).toFixed(2)}
                                <div className="text-xs text-gray-500">
                                    ({calculateSqft().toFixed(2)} sqft)
                                </div>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={handleCalculate}
                            disabled={calculateSqft() <= 0}
                            className="w-full bg-blue-800 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500"
                        >
                            {t('calculators.sanitise.addToBooking')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SanitiseCalculator; 